import { BarChart2, MapPin, Bot, Zap, Lock, Star } from "lucide-react";

/**
 * Shared section components for the /get-found sales page. The page renders
 * a dark, conversion-focused layout; these sections match the jeremyhaynesai
 * reference while using Outpost branding. Colors are kept inline to match the
 * rest of the page (dark ink background + orange accent).
 *
 * The results gallery lives in its own module (GetFoundResults.tsx) so the
 * sales page can place it where it wants without pulling the rest of these
 * sections.
 */

export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#b85736] font-bold">
      {children}
    </span>
  );
}

export function SectionHeading({
  children,
  className = "text-white",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2
      className={`font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight leading-tight ${className}`}
    >
      {children}
    </h2>
  );
}

export function ThreeWaysSection() {
  const ways = [
    {
      icon: BarChart2,
      label: "Google Search",
      title: "Rank on the regular results page",
      body: "Get your site to a 100 SEO score. Fix the exact checks Google runs — title tags, schema, speed, indexability — with step-by-step platform instructions.",
    },
    {
      icon: MapPin,
      label: "Google Maps",
      title: "Win the map pack",
      body: "60–70% of local clicks go to the top 3 map results. Score your Google Business Profile against the factors that actually move you into them.",
    },
    {
      icon: Bot,
      label: "AI & ChatGPT",
      title: "Get recommended by AI",
      body: "People now ask ChatGPT 'who's the best [service] near me?' Find out if AI crawlers can read your site, and fix it if they can't.",
    },
  ];
  return (
    <section className="bg-[#111118] border-y border-white/10 px-5 md:px-8 py-16 md:py-24">
      <div className="max-w-5xl mx-auto">
        <Eyebrow>Three Ways You Get Found</Eyebrow>
        <SectionHeading className="text-white mt-2">
          One playbook. Every front door.
        </SectionHeading>
        <p className="mt-4 max-w-2xl text-white/60 text-sm md:text-base">
          Your customers search three different ways. Most businesses only chase
          one. The Get Found Playbook covers all three.
        </p>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {ways.map((w) => (
            <div
              key={w.label}
              className="bg-black/40 border border-white/10 p-6 space-y-4 rounded-xl hover:border-[#b85736]/40 transition-colors"
            >
              <div className="inline-flex items-center gap-2">
                <w.icon className="h-6 w-6 text-[#b85736]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 font-bold">
                  {w.label}
                </span>
              </div>
              <h3 className="font-heading font-extrabold text-lg uppercase tracking-tight text-white leading-tight">
                {w.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrainingDataSection() {
  const stats = [
    { value: "28", label: "Page playbook" },
    { value: "100", label: "Point Google checklist" },
    { value: "20", label: "Pre-filled AI prompts" },
    { value: "30", label: "Core local pages mapped" },
  ];
  return (
    <section className="px-5 md:px-8 pb-16 md:pb-24 max-w-5xl mx-auto pt-0">
      <Eyebrow>Built On Real Ranking Data</Eyebrow>
      <SectionHeading className="text-white mt-2">
        Not theory. A system you run.
      </SectionHeading>
      <p className="mt-4 max-w-2xl text-white/60 text-sm md:text-base">
        Every recommendation comes from how Google and AI actually rank local
        businesses today — not outdated advice that stopped working years ago.
      </p>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="bg-[#111118] border border-white/10 p-5 rounded-xl text-center"
          >
            <p className="font-heading font-extrabold text-4xl md:text-5xl text-[#b85736] leading-none">
              {s.value}
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">
              {s.label}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex items-start gap-3 bg-[#b85736]/10 border-l-4 border-[#b85736] p-5 rounded-r-lg">
        <Zap className="h-5 w-5 text-[#b85736] mt-0.5 shrink-0" />
        <p className="text-white/80 text-sm leading-relaxed">
          <span className="text-white font-semibold">No guesswork.</span> Your
          audit ranks every issue by impact and effort, then links each one to
          the exact prompt that fixes it.
        </p>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  const quotes = [
    {
      name: "Local service owner",
      role: "After running the audit",
      body: "Found out my homepage title literally said 'Home.' Fixed it in five minutes. The audit told me exactly what Google couldn't read.",
    },
    {
      name: "First-time playbook user",
      role: "Two weeks in",
      body: "The pre-filled prompts saved me hours. I pasted one into ChatGPT and got a full service page with my city and services already in it.",
    },
    {
      name: "Map-pack newcomer",
      role: "After the Core 30 plan",
      body: "I didn't know I was missing service pages. The plan showed me exactly which ones to build and the prompt wrote them.",
    },
  ];
  return (
    <section className="px-5 md:px-8 py-16 md:py-24 max-w-5xl mx-auto">
      <Eyebrow>From Real Use</Eyebrow>
      <SectionHeading className="text-white mt-2">
        What changes when you run it
      </SectionHeading>
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        {quotes.map((q) => (
          <figure
            key={q.name}
            className="bg-[#111118] border border-white/10 p-6 rounded-xl flex flex-col gap-4"
          >
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className="h-4 w-4 fill-[#b85736] text-[#b85736]"
                />
              ))}
            </div>
            <blockquote className="text-white/75 text-sm leading-relaxed flex-1">
              "{q.body}"
            </blockquote>
            <figcaption className="border-t border-white/10 pt-3">
              <p className="font-heading font-extrabold text-sm uppercase tracking-tight text-white">
                {q.name}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#b85736] mt-0.5">
                {q.role}
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-6 flex items-center justify-center gap-2 font-mono text-[11px] text-white/40 text-center">
        <Lock className="h-3.5 w-3.5" /> Honest results, not ranking guarantees.
      </p>
    </section>
  );
}
